import React, { useState, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { fetchWrapper } from "../utils/fetchWrapper";
import { MdTrendingUp, MdFilterList, MdDateRange, MdBusiness } from "react-icons/md";
import SearchableSelect from "../components/ui/SearchableSelect";
import "../components/ui/SearchableSelect.css";
import "./AnalysisPage.css";

const AnalysisPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isComparing, setIsComparing] = useState(false);
  const [compareType, setCompareType] = useState("project"); // "project" or "period"
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    const start = new Date();
    start.setDate(now.getDate() - 7);
    return {
      period: "7",
      startDate: start.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
      projectDomain: "",
      projectDomain2: "",
      startDate2: "",
      endDate2: "",
    };
  });

  const [summary, setSummary] = useState({
    total: 0,
    peak: 0,
    average: 0,
    compareTotal: 0,
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    // If comparing periods, initialize the second date range to the period before the primary range
    if (isComparing && compareType === "period" && (!filters.startDate2 || !filters.endDate2)) {
      const s1 = new Date(filters.startDate);
      const e1 = new Date(filters.endDate);
      const duration = e1.getTime() - s1.getTime();
      
      const e2 = new Date(s1.getTime() - 86400000); // 1 day before s1
      const s2 = new Date(e2.getTime() - duration);
      
      setFilters(prev => ({
        ...prev,
        startDate2: s2.toISOString().split('T')[0],
        endDate2: e2.toISOString().split('T')[0]
      }));
    }
  }, [isComparing, compareType, filters.startDate, filters.endDate]);

  useEffect(() => {
    fetchStats();
  }, [
    filters.projectDomain, 
    filters.startDate, 
    filters.endDate, 
    isComparing, 
    compareType, 
    filters.projectDomain2, 
    filters.startDate2, 
    filters.endDate2
  ]);

  const fetchProjects = async () => {
    try {
      const res = await fetchWrapper.get("/projects");
      setProjects(res.data || []);
    } catch (err) {
      console.error("Failed to fetch projects", err);
    }
  };

  const fetchStats = async () => {
    if (!filters.startDate || !filters.endDate) return;
    setLoading(true);
    try {
      let url = `/analytics/leads-trend?startDate=${filters.startDate}&endDate=${filters.endDate}`;
      
      if (filters.projectDomain) {
        url += `&projectDomain=${filters.projectDomain}`;
      }

      const comparisonActive = isComparing && (
        (compareType === "project" && filters.projectDomain2) ||
        (compareType === "period" && filters.startDate2 && filters.endDate2)
      );

      if (comparisonActive) {
        if (compareType === "project") {
            url += `&projectDomain2=${filters.projectDomain2}`;
        } else {
            url += `&startDate2=${filters.startDate2}&endDate2=${filters.endDate2}`;
        }
      }

      const res = await fetchWrapper.get(url);
      if (res.data.success) {
        const rawData1 = res.data.data;
        const rawData2 = res.data.compareData;
        
        let chartData = [];

        if (!comparisonActive) {
            chartData = rawData1.map(item => ({
                name: item._id,
                count: item.count,
                date: item._id,
            }));
            const total = chartData.reduce((acc, curr) => acc + curr.count, 0);
            const peak = chartData.length > 0 ? Math.max(...chartData.map(d => d.count)) : 0;
            const average = chartData.length > 0 ? (total / chartData.length).toFixed(1) : 0;
            setSummary({ total, peak, average, compareTotal: 0 });
        } else if (compareType === "project") {
            const dateMap = {};
            rawData1.forEach(item => {
                dateMap[item._id] = { name: item._id, date: item._id, count: item.count, count2: 0 };
            });
            if (rawData2) {
                rawData2.forEach(item => {
                    if (dateMap[item._id]) {
                        dateMap[item._id].count2 = item.count;
                    } else {
                        dateMap[item._id] = { name: item._id, date: item._id, count: 0, count2: item.count };
                    }
                });
            }
            chartData = Object.values(dateMap).sort((a,b) => a.date.localeCompare(b.date));
            
            const total = rawData1.reduce((acc, curr) => acc + curr.count, 0);
            const compareTotal = rawData2 ? rawData2.reduce((acc, curr) => acc + curr.count, 0) : 0;
            const peak = Math.max(...chartData.map(d => Math.max(d.count, d.count2)));
            setSummary({ total, peak, average: (total / rawData1.length || 0).toFixed(1), compareTotal });
        } else if (compareType === "period") {
            const r2 = rawData2 || [];
            const maxLength = Math.max(rawData1.length, r2.length);
            for (let i = 0; i < maxLength; i++) {
                chartData.push({
                    name: `Day ${i + 1}`,
                    count: rawData1[i]?.count || 0,
                    count2: r2[i]?.count || 0,
                    date1: rawData1[i]?._id || "-",
                    date2: r2[i]?._id || "-",
                });
            }
            const total = rawData1.reduce((acc, curr) => acc + curr.count, 0);
            const compareTotal = r2.reduce((acc, curr) => acc + curr.count, 0);
            const peak = Math.max(...chartData.map(d => Math.max(d.count, d.count2)));
            setSummary({ total, peak, average: (total / rawData1.length || 0).toFixed(1), compareTotal });
        }

        setData(chartData);
      }
    } catch (err) {
      console.error("Failed to fetch leads trend", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (e, target = "primary") => {
    const val = e.target.value;
    if (val === "custom") {
        if (target === "primary") setFilters(prev => ({ ...prev, period: val }));
        return;
    }

    const now = new Date();
    const start = new Date();
    const days = parseInt(val);
    start.setDate(now.getDate() - days);

    if (target === "primary") {
        setFilters(prev => ({ 
            ...prev, 
            period: val, 
            startDate: start.toISOString().split('T')[0], 
            endDate: now.toISOString().split('T')[0] 
        }));
    } else {
        setFilters(prev => ({ 
            ...prev, 
            startDate2: start.toISOString().split('T')[0], 
            endDate2: now.toISOString().split('T')[0] 
        }));
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="label">{label}</p>
          {payload.map((p, idx) => {
            const isRange2 = p.dataKey === "count2";
            const seriesName = isRange2 
              ? (compareType === "project" ? (filters.projectDomain2 || "Project 2") : "Previous Range")
              : (filters.projectDomain || "Current Range");
            const seriesDate = isRange2 ? dataPoint.date2 : (dataPoint.date || dataPoint.date1);

            return (
              <p key={idx} className="intro" style={{ color: p.color }}>
                {seriesName} {seriesDate && seriesDate !== "-" ? `(${seriesDate})` : ""}: {p.value}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="analysis-page">
      <div className="analysis-header">
        <div className="title-section">
          <h2><MdTrendingUp className="me-2" />Leads Analysis</h2>
          <p className="text-muted">Visualize and compare lead generation performance</p>
        </div>

        <div className="filter-card">
          <div className="filter-section card-base">
            <div className="filter-item">
              <label><MdDateRange className="me-1" />Shortcuts</label>
              <select value={filters.period} onChange={(e) => handlePeriodChange(e, "primary")} className="form-select-sm">
                <option value="custom">Custom Range</option>
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
              </select>
            </div>

            <div className="filter-item">
              <label>Start Date</label>
              <input 
                type="date" 
                value={filters.startDate} 
                onChange={(e) => setFilters(prev => ({ ...prev, period: "custom", startDate: e.target.value }))}
                className="form-control-sm"
              />
            </div>
            <div className="filter-item">
              <label>End Date</label>
              <input 
                type="date" 
                value={filters.endDate} 
                onChange={(e) => setFilters(prev => ({ ...prev, period: "custom", endDate: e.target.value }))}
                className="form-control-sm"
              />
            </div>

            <div className="filter-item">
              <label><MdBusiness className="me-1" />Project</label>
              <SearchableSelect
                value={filters.projectDomain}
                onChange={(val) => setFilters(prev => ({ ...prev, projectDomain: val }))}
                options={[
                  { value: "", label: "All Projects" },
                  ...projects.map(p => ({ value: p.domain, label: p.projectName || p.domain }))
                ]}
                placeholder="All Projects"
                searchPlaceholder="Search projects..."
              />
            </div>

            <div className="filter-item d-flex align-items-center mb-0" style={{ alignSelf: 'center', marginTop: '18px' }}>
                <div className="form-check form-switch">
                    <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="compareToggle" 
                        checked={isComparing} 
                        onChange={(e) => setIsComparing(e.target.checked)} 
                    />
                    <label className="form-check-label small fw-bold" htmlFor="compareToggle">Compare</label>
                </div>
            </div>
          </div>

          {isComparing && (
            <div className="filter-section compare-section card-base mt-2">
              <div className="filter-item">
                <label>Compare By</label>
                <select value={compareType} onChange={(e) => setCompareType(e.target.value)} className="form-select-sm">
                  <option value="project">Different Project</option>
                  <option value="period">Different Period</option>
                </select>
              </div>

              {compareType === "project" ? (
                <div className="filter-item">
                  <label><MdBusiness className="me-1" />Project 2</label>
                  <SearchableSelect
                    value={filters.projectDomain2}
                    onChange={(val) => setFilters(prev => ({ ...prev, projectDomain2: val }))}
                    options={[
                      { value: "", label: "Select Project..." },
                      ...projects.map(p => ({ value: p.domain, label: p.projectName || p.domain }))
                    ]}
                    placeholder="Select Project..."
                    searchPlaceholder="Search projects..."
                  />
                </div>
              ) : (
                <>
                  <div className="filter-item">
                    <label>Start Date 2</label>
                    <input 
                      type="date" 
                      value={filters.startDate2} 
                      onChange={(e) => setFilters(prev => ({ ...prev, startDate2: e.target.value }))}
                      className="form-control-sm"
                    />
                  </div>
                  <div className="filter-item">
                    <label>End Date 2</label>
                    <input 
                      type="date" 
                      value={filters.endDate2} 
                      onChange={(e) => setFilters(prev => ({ ...prev, endDate2: e.target.value }))}
                      className="form-control-sm"
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="summary-grid">
        <div className="summary-card">
          <span className="label">Total Leads {isComparing && "(S1)"}</span>
          <h3 className="value">{summary.total}</h3>
        </div>
        {isComparing && (
           <div className="summary-card compare">
             <span className="label">Total Leads (S2)</span>
             <h3 className="value">{summary.compareTotal}</h3>
           </div>
        )}
        <div className="summary-card">
          <span className="label">Daily Average</span>
          <h3 className="value">{summary.average}</h3>
        </div>
        <div className="summary-card">
          <span className="label">Peak Performance</span>
          <h3 className="value">{summary.peak}</h3>
        </div>
      </div>

      <div className="chart-container card-base">
        <div className="chart-header">
          <h4>{isComparing ? "Trend Comparison" : "Leads Trend"}</h4>
          <span className="badge-period">
            {filters.period === "custom" 
              ? `${filters.startDate || '...'} to ${filters.endDate || '...'}`
              : `Last ${filters.period} Days`}
          </span>
        </div>
        
        <div className="chart-wrapper">
          {loading ? (
            <div className="loader-container">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : data.length === 0 ? (
            <div className="no-data">No lead data found for the selected period</div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4a90e2" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#4a90e2" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorLeadsCompare" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2ecc71" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#2ecc71" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: '#666', fontSize: 12 }}
                        minTickGap={20}
                    />
                    <YAxis 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: '#666', fontSize: 12 }} 
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#4a90e2', strokeWidth: 1 }} />
                    <Area 
                        type="monotone" 
                        dataKey="count" 
                        stroke="#4a90e2" 
                        strokeWidth={3}
                        fillOpacity={1} 
                        fill="url(#colorLeads)" 
                        animationDuration={1500}
                    />
                    {isComparing && (
                        <Area 
                            type="monotone" 
                            dataKey="count2" 
                            stroke="#2ecc71" 
                            strokeWidth={3}
                            fillOpacity={1} 
                            fill="url(#colorLeadsCompare)" 
                            animationDuration={1500}
                        />
                    )}
                </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalysisPage;
